<template>
  <div id="app">
    <!-- 表情市场页面：不显示侧边栏和顶部栏 -->
    <div v-if="currentPath === '/market'">
      <router-view />
    </div>
    <!-- 登录页面：不显示侧边栏和顶部栏 -->
    <div v-else-if="currentPath === '/login'">
      <router-view />
    </div>
    <!-- 其他页面：显示完整布局 -->
    <el-container v-else-if="isLoggedIn" style="height: 100vh;">
      <LayoutSidebar />
      <el-main class="main-content">
        <div class="topbar">
          <div class="spacer"></div>
          <el-dropdown @command="handleCommand">
            <div class="user-chip">
              <el-avatar :size="28" :src="currentUser.avatar_url || ''">
                {{ (currentUser.nickname || currentUser.username || 'U').charAt(0).toUpperCase() }}
              </el-avatar>
              <span class="name">{{ currentUser.nickname || currentUser.username || '用户' }}</span>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">个人信息</el-dropdown-item>
                <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
        <router-view />
      </el-main>
    </el-container>
    <div v-else>
      <router-view />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, provide } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { getCurrentUser } from './api/auth';
import LayoutSidebar from './components/LayoutSidebar.vue';
import { getUnhandledCount } from '@/api/deviceRoleSetting';
export default {
  name: 'App',
  components: { LayoutSidebar },
  setup() {
    const router = useRouter();
    const route = useRoute();
    
    const currentUser = ref({ username: '', role: '', avatar: '' });
    const isLoggedIn = ref(!!localStorage.getItem('token'));
    
    // 计算当前路径，避免在模板中直接访问可能未定义的 route
    const currentPath = computed(() => route?.path || '');

    // 更新登录状态和用户信息
    const updateUserInfo = async () => {
      const token = localStorage.getItem('token');
      isLoggedIn.value = !!token;
      
      console.log('🔄 updateUserInfo 被调用，token:', token ? '存在' : '不存在', '当前路径:', route.path);
      
      if (token) {
        try {
          // 使用 skipAutoMessage=true，避免 401 错误时立即清除 token 和跳转
          console.log('📡 准备调用 getCurrentUser(true)...');
          const res = await getCurrentUser(true);
          if (res && res.success) {
            console.log('✅ getCurrentUser 成功:', res.data);
            currentUser.value = res.data || {};
            localStorage.setItem('user', JSON.stringify(currentUser.value));
            // 用户信息加载后，如果是管理员，获取未处理记录数量
            if (currentUser.value.role === 'admin') {
              getRoleSettingMessageNumber();
            }
            return;
          } else {
            console.warn('⚠️ getCurrentUser 返回失败:', res);
          }
        } catch (e) {
          // 如果 getCurrentUser 失败（可能是 401），先尝试使用本地缓存
          // 不要立即清除 token，因为可能是网络问题或后端还没准备好
          // 检查错误类型，如果是 401 且 skipAutoMessage 为 true，说明我们已经处理了
          if (e.response && e.response.status === 401) {
            console.warn('⚠️ getCurrentUser 失败：401 未授权，使用本地缓存（skipAutoMessage已启用，不会清除token）');
          } else {
            console.warn('⚠️ getCurrentUser 失败，使用本地缓存:', e);
          }
        }
        // 回退到本地缓存
        try {
          const userStr = localStorage.getItem('user');
          currentUser.value = userStr && userStr !== 'undefined' ? JSON.parse(userStr) : {};
          // 如果是管理员，获取未处理记录数量（需要再次检查 token，因为可能已经退出）
          if (currentUser.value.role === 'admin' && localStorage.getItem('token')) {
            getRoleSettingMessageNumber();
          }
        } catch (e) {
          currentUser.value = {};
        }
      } else {
        currentUser.value = {};
        // 清除未处理记录数量
        roleSettingMessageNumber.value = 0;
      }
    };

    const getPageTitle = () => {
      const routeTitles = {
        '/dashboard': '仪表盘',
        '/users': '用户管理',
        '/playlists': '歌单管理',
        '/playlist-categories': '歌单类别',
        '/playlist-tags': '歌单标签',
        '/songs': '歌曲管理',
        '/packs': '表情包管理',
        '/devices': '设备管理',
        '/audit': '审计日志',
        '/profile': '个人管理',
        '/deviceRoleSetting': '角色配置记录',
        '/deviceSchedule': '课程表',
        '/deviceBoard': '留言板',
        '/themes': '主题管理',
        '/menus': '小程序菜单管理',
        '/weather': '天气 Key 管理',
        '/iot-server-api-doc': 'IoT API 文档',
      };
      return routeTitles[route.path] || '多媒体管理平台';
    };

    const handleCommand = (command) => {
      if (command === 'logout') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        isLoggedIn.value = false;
        currentUser.value = {};
        window.dispatchEvent(new Event('localStorageChange'));
        router.push('/login');
      } else if (command === 'profile') {
        router.push('/profile');
      }
    };

    // 监听路由变化，当从登录页跳转时更新用户信息
    watch(() => route.path, (newPath, oldPath) => {
      // 如果从登录页跳转到其他页面，说明刚登录成功，需要更新用户信息
      if (oldPath === '/login' && newPath !== '/login') {
        // 延迟执行，确保 token 已保存且后端已准备好
        // 避免立即调用 getCurrentUser 导致 401 错误
        setTimeout(() => {
          updateUserInfo();
        }, 500);
      }
    }, { immediate: false });
    
    // 监听 localStorage 变化
    // 1. storage 事件：用于跨窗口/标签页的变化（浏览器原生事件）
    // 2. localStorageChange 事件：用于同窗口内的变化（自定义事件）
    const handleStorageChange = (e) => {
      // storage 事件有 key 属性，localStorageChange 事件没有
      if (!e.key || e.key === 'token' || e.key === 'user') {
        // 延迟执行，避免登录后立即调用导致 401 错误
        // 给后端一些时间准备 token 验证
        setTimeout(() => {
          console.log('🔄 handleStorageChange 触发，准备更新用户信息');
          updateUserInfo();
        }, 500);
      }
    };
    
    // 添加 storage 事件监听（用于跨窗口/标签页的 localStorage 变化）
    window.addEventListener('storage', handleStorageChange);
    // 添加自定义事件监听（用于同窗口内的 localStorage 变化）
    window.addEventListener('localStorageChange', handleStorageChange);

    onMounted(() => {
      updateUserInfo();
    });
    
    onUnmounted(() => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleStorageChange);
    });

    const roleSettingMessageNumber = ref(0);//角色配置未处理记录数量
    // 获取角色配置未处理记录数量（仅管理员）
    const getRoleSettingMessageNumber = () => {
      // 检查 token 是否存在，如果不存在说明已退出登录，不调用接口
      const token = localStorage.getItem('token');
      if (!token) {
        roleSettingMessageNumber.value = 0;
        return;
      }
      // 只有管理员才调用这个接口
      if (currentUser.value.role === 'admin') {
        getUnhandledCount().then(res => { 
          roleSettingMessageNumber.value = res.data || 0;
        }).catch(err => {
          // 忽略错误，避免普通用户看到403错误
          console.debug('获取未处理记录数量失败（可能是权限问题）:', err);
        });
      }
    };

    
    provide('roleSettingMessageNumber', roleSettingMessageNumber);
    provide('refreshRoleSettingMessageNumber', getRoleSettingMessageNumber);

    
    // 提供更新用户信息的方法，供其他组件调用
    provide('updateUserInfo', updateUserInfo);
    
    return {
      currentUser,
      isLoggedIn,
      currentPath,
      getPageTitle,
      handleCommand
    };
  }
};
</script>

<style>
html, body, #app {
  height: 100%;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

* {
  box-sizing: border-box;
}

.main-content {
  background-color: #f5f7fa;
  padding: 20px;
  overflow-y: auto;
  height: 100vh;
}

.topbar { display: flex; align-items: center; justify-content: flex-end; margin-bottom: 12px; }
.user-chip { display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: #fff; border: 1px solid #e6e6e6; border-radius: 20px; cursor: pointer; }
.user-chip .name { color: #606266; font-size: 13px; }

/* ========== 全局表格优化样式 ========== */
/* 减少表格行高，让信息更紧凑 */
.el-table {
  font-size: 13px;
}

.el-table th.el-table__cell {
  padding: 8px 0;
  font-size: 13px;
  font-weight: 600;
  background-color: #f5f7fa !important;
}

.el-table td.el-table__cell {
  padding: 6px 0;
  font-size: 13px;
}

/* 减少表格单元格内边距 */
.el-table .cell {
  padding: 0 8px;
  line-height: 1.4;
}

/* 优化按钮大小和间距 */
.el-table .el-button {
  padding: 4px 10px;
  font-size: 12px;
  height: 24px;
  margin: 0 2px;
}

.el-table .el-button + .el-button {
  margin-left: 4px;
}

/* 优化标签大小 */
.el-table .el-tag {
  padding: 0 6px;
  height: 20px;
  line-height: 20px;
  font-size: 12px;
}

/* 优化输入框和选择器大小 */
.el-table .el-input__inner {
  height: 28px;
  line-height: 28px;
  font-size: 13px;
}

/* 优化分页器 */
.el-pagination {
  padding: 12px 0;
}

.el-pagination .el-pagination__total,
.el-pagination .el-pagination__jump {
  font-size: 13px;
}

/* 优化搜索栏间距 */
.el-row {
  margin-bottom: 12px !important;
}

/* 优化表格容器，减少页面内边距 */
.user-management,
.device-management,
.song-management,
.playlist-list,
.emotion-management,
.weather-management,
.menu-management,
.theme-management {
  padding: 16px !important;
}

/* 优化对话框内表格 */
.el-dialog .el-table {
  font-size: 13px;
}

.el-dialog .el-table th.el-table__cell {
  padding: 6px 0;
}

.el-dialog .el-table td.el-table__cell {
  padding: 4px 0;
}

/* 优化头像大小 */
.el-table .el-avatar {
  width: 24px !important;
  height: 24px !important;
}

/* 优化图标大小 */
.el-table .el-icon {
  font-size: 14px;
}

/* 优化操作列，让按钮更紧凑 */
.el-table .el-table__cell:last-child .cell {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  align-items: center;
}

/* 表格内按钮更紧凑 */
.el-table .el-button {
  padding: 4px 10px;
  margin: 0 2px;
}

/* 优化表格空状态 */
.el-table__empty-block {
  padding: 20px 0;
}

/* 优化表格加载状态 */
.el-loading-mask {
  background-color: rgba(255, 255, 255, 0.8);
}

/* 优化表格斑马纹 */
.el-table--striped .el-table__body tr.el-table__row--striped td {
  background-color: #fafafa;
}

/* 优化表格悬停效果 */
.el-table__body tr:hover > td {
  background-color: #f5f7fa !important;
}
</style>
